import React, { useEffect, useState } from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';

import { withDialogFooterSpacing } from 'ui/shared/dialogFooterSpacing';

interface StartPointDialogProps {
  visible: boolean;
  onHide: () => void;
  onApply: (latitude: number | null, longitude: number | null) => void;
  initialLatitude: number | null;
  initialLongitude: number | null;
}

const StartPointDialog: React.FC<StartPointDialogProps> = ({
  visible,
  onHide,
  onApply,
  initialLatitude,
  initialLongitude
}) => {
  const [latitude, setLatitude] = useState<number | null>(initialLatitude);
  const [longitude, setLongitude] = useState<number | null>(initialLongitude);
  const [latitudeError, setLatitudeError] = useState<string>('');
  const [longitudeError, setLongitudeError] = useState<string>('');

  useEffect(() => {
    if (visible) {
      setLatitude(initialLatitude);
      setLongitude(initialLongitude);
      setLatitudeError('');
      setLongitudeError('');
    }
  }, [visible, initialLatitude, initialLongitude]);

  const validateLatitude = (value: number | null): boolean => {
    if (longitude !== null && longitude !== undefined && (value === null || value === undefined)) {
      setLatitudeError('Укажите широту');
      return false;
    }

    if ((value === null || value === undefined) && (longitude === null || longitude === undefined)) {
      setLatitudeError('');
      return true;
    }

    if (value !== null && value !== undefined) {
      if (value < -90 || value > 90) {
        setLatitudeError('Широта должна быть в диапазоне от -90 до 90');
        return false;
      }
    }

    setLatitudeError('');
    return true;
  };

  const validateLongitude = (value: number | null): boolean => {
    if (latitude !== null && latitude !== undefined && (value === null || value === undefined)) {
      setLongitudeError('Укажите долготу');
      return false;
    }

    if ((value === null || value === undefined) && (latitude === null || latitude === undefined)) {
      setLongitudeError('');
      return true;
    }
    if (value !== null && value !== undefined) {
      if (value < -180 || value > 180) {
        setLongitudeError('Долгота должна быть в диапазоне от -180 до 180');
        return false;
      }
    }

    setLongitudeError('');
    return true;
  };

  const handleLatitudeChange = (value: number | null) => {
    setLatitude(value);
    validateLatitude(value);
    validateLongitude(longitude);
  };

  const handleLongitudeChange = (value: number | null) => {
    setLongitude(value);
    validateLongitude(value);
    validateLatitude(latitude);
  };

  const handleApply = () => {
    const isLatValid = validateLatitude(latitude);
    const isLonValid = validateLongitude(longitude);
    
    if (!isLatValid || !isLonValid) {
      return;
    }
    
    onApply(latitude, longitude);
    onHide();
  };

  const handleCancel = () => {
    setLatitude(initialLatitude);
    setLongitude(initialLongitude);
    setLatitudeError('');
    setLongitudeError('');
    onHide();
  };

  const isApplyDisabled = !!latitudeError || !!longitudeError;

  const footer = (
    <div>
      <Button 
        label="Применить" 
        onClick={handleApply} 
        disabled={isApplyDisabled}
      />
      <Button label="Отмена" onClick={handleCancel} className="p-button-secondary" />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={handleCancel}
      header="Стартовая точка"
      modal
      footer={footer}
      pt={withDialogFooterSpacing()}
      style={{ width: '450px' }}
    >
      <div className="flex flex-column">
        <div className="flex flex-column">
          <div className="flex flex-row justify-content-between align-items-center gap-3">
            <label style={{ minWidth: '80px' }}>Широта:</label>
            <InputNumber
              className="w-full"
              inputClassName="w-full"
              maxFractionDigits={14}
              locale="en"
              useGrouping={false}
              value={latitude}
              onValueChange={(e) => {
                handleLatitudeChange(e.value ?? null);
              }}
            />
          </div>
          <div className="text-red-500 text-sm mt-1" style={{ paddingLeft: '80px', height: 16 }}>
            {latitudeError}
          </div>
        </div>

        <div className="flex flex-column">
          <div className="flex flex-row justify-content-between align-items-center gap-3">
            <label style={{ minWidth: '80px' }}>Долгота:</label>
            <InputNumber
              className="w-full"
              inputClassName="w-full"
              maxFractionDigits={14}
              locale="en"
              useGrouping={false}
              value={longitude}
              onValueChange={(e) => {
                handleLongitudeChange(e.value ?? null);
              }}
            />
          </div>
          <div className="text-red-500 text-sm mt-1" style={{ paddingLeft: '80px', height: 16 }}>
            {longitudeError}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default StartPointDialog;